<template>
  <section v-if="props.postType !== SocialPostPublishType.Story" class="post-locations">
    <div class="post-locations__heading">
      <div class="post-locations__title-row">
        <h4 class="global-h4 post-locations__title">Tags and Mentions</h4>
        <InfoIconWithTooltip :width="280">Mentions are added to the end of the caption when the post publishes. You can tag up to {{ MAX_TAGGED_LOCATIONS }} accounts.</InfoIconWithTooltip>
      </div>
      <span v-if="settings.length && isAtTaggedLocationLimit" class="post-locations__max-hint">
        <InfoIconWithTooltip :width="280">Social networks may treat excessive tagging as spam, so we limit each post to {{ MAX_TAGGED_LOCATIONS }} relevant accounts.</InfoIconWithTooltip>
        {{ MAX_TAGGED_LOCATIONS }} tags max
      </span>
      <LinkWithIcon v-else-if="settings.length" color="gray" isNoUnderline :isDisabled="props.isDisabled || isOpeningLocationSelector" @click="openLocationSelector">
        <template #icon>
          <LoadingIcon v-if="isOpeningLocationSelector" :size="14" />
          <IconEmbedded v-else name="plus_3" :size="14" />
        </template>
        Add more
      </LinkWithIcon>
    </div>

    <Loader v-if="isLoading" />
    <div v-else-if="settings.length" class="post-locations__cards">
      <article v-for="setting in settings" :key="setting.locationId" class="post-locations__card">
        <header
          class="post-locations__card-header"
          role="button"
          tabindex="0"
          :aria-expanded="isOptionsExpanded(setting.locationId)"
          :aria-label="isOptionsExpanded(setting.locationId) ? 'Collapse location options' : 'Expand location options'"
          @click="toggleOptionsExpanded(setting.locationId)"
          @keydown.enter.prevent="toggleOptionsExpanded(setting.locationId)"
          @keydown.space.prevent="toggleOptionsExpanded(setting.locationId)"
        >
          <LocationAvatar :src="locationMap.get(setting.locationId)?.icon" :name="locationMap.get(setting.locationId)?.name" :size="44" />
          <div class="post-locations__title-copy">
            <strong style="font-size: 15px">{{ locationMap.get(setting.locationId)?.name || "Unset" }}</strong>
            <LocationSocialHandles :location="locationMap.get(setting.locationId)" />
          </div>
          <div class="post-locations__header-actions">
            <button type="button" class="post-locations__header-action" :disabled="props.isDisabled" @click.stop="openAdjust(setting.locationId)">Edit</button>
            <button type="button" class="post-locations__header-action post-locations__remove" :disabled="props.isDisabled" @click.stop="removeLocation(setting.locationId)">Remove</button>
          </div>
          <span class="post-locations__collapse" aria-hidden="true">
            <IconEmbedded :name="isOptionsExpanded(setting.locationId) ? 'caret-top_2-5' : 'caret-bottom_2-5'" :size="16" />
          </span>
        </header>

        <div v-if="isOptionsExpanded(setting.locationId)">
          <div class="post-locations__option">
            <div class="post-locations__option-label">
              <IconEmbedded name="mention_2" :size="26" color="#667085" />
              <div class="post-locations__mention-label">
                <strong>Mention in caption:</strong>
                <span v-if="mentionNetworks(locationMap.get(setting.locationId)).length" class="post-locations__network-icons" aria-hidden="true">
                  <IconEmbedded v-for="channel in mentionNetworks(locationMap.get(setting.locationId))" :key="channel" :name="channel" :size="15" color="#667085" />
                </span>
                <small v-else>No social handles added</small>
              </div>
            </div>
            <SrpSwitch
              :isChecked="setting.addCaptionMention"
              :isDisabled="props.isDisabled || !canMention(locationMap.get(setting.locationId))"
              @update:isChecked="setMention(setting.locationId, $event)"
            />
          </div>
          <div v-if="showInstagramTagRow(locationMap.get(setting.locationId))" class="post-locations__option">
            <div class="post-locations__option-label" style="margin-left: -4px">
              <IconEmbedded name="tag-person_1-5" :size="32" color="#667085" />
              <div class="post-locations__instagram-tag-copy">
                <div class="post-locations__instagram-tag-title">
                  <strong>Tag Instagram photos</strong>
                  <SrpDropdown
                    class="post-locations__tag-scope"
                    :class="{ 'post-locations__tag-scope--disabled': props.isDisabled || setting.instagramTag == null }"
                    placement="bottom-start"
                    :offset="8"
                    :width="160"
                    :isMobileModalCloseButtonVisible="false"
                  >
                    <template #clickableElement="{ isDropdownVisible }">
                      <button
                        type="button"
                        class="post-locations__gear"
                        :disabled="props.isDisabled || setting.instagramTag == null"
                        :aria-label="photosToTagLabel(setting)"
                        :aria-expanded="isDropdownVisible"
                      >
                        <IconEmbedded name="gear_2" :size="18" color="#667085" />
                      </button>
                    </template>
                    <template #dropdown="{ close }">
                      <div class="post-locations__tag-scope-menu" role="menu">
                        <button
                          type="button"
                          class="post-locations__tag-scope-option"
                          role="menuitemradio"
                          :aria-checked="effectivePhotosToTag(setting) === InstagramPhotosToTag.First"
                          @click="
                            setPhotosToTag(setting.locationId, InstagramPhotosToTag.First);
                            close();
                          "
                        >
                          <span>First photo</span>
                          <IconEmbedded v-if="effectivePhotosToTag(setting) === InstagramPhotosToTag.First" name="check_3" :size="16" color="#058587" />
                        </button>
                        <button
                          type="button"
                          class="post-locations__tag-scope-option"
                          role="menuitemradio"
                          :aria-checked="effectivePhotosToTag(setting) === InstagramPhotosToTag.All"
                          @click="
                            setPhotosToTag(setting.locationId, InstagramPhotosToTag.All);
                            close();
                          "
                        >
                          <span>All photos</span>
                          <IconEmbedded v-if="effectivePhotosToTag(setting) === InstagramPhotosToTag.All" name="check_3" :size="16" color="#058587" />
                        </button>
                      </div>
                    </template>
                  </SrpDropdown>
                </div>
                <small v-if="setting.instagramTag != null" style="margin-left: 3px; margin-top: -5px">{{ photosToTagSummary(setting) }}</small>
              </div>
            </div>
            <SrpSwitch :isChecked="setting.instagramTag != null" :isDisabled="props.isDisabled" @update:isChecked="setInstagramTag(setting.locationId, $event)" />
          </div>
          <!-- Warn only; the switch stays usable so the tag applies on its own once the account is public and re-checked. -->
          <NoteWithIcon
            v-if="setting.instagramTag != null && showInstagramTagRow(locationMap.get(setting.locationId)) && isInstagramPrivate(locationMap.get(setting.locationId))"
            class="post-locations__private-note"
            color="yellow"
            size="tiny"
            icon="warning_2-5"
          >
            <template #text>Private account, must be public for photo tagging.</template>
          </NoteWithIcon>
        </div>
      </article>
    </div>
    <div
      v-else
      class="post-locations__empty"
      :class="{ 'post-locations__empty--disabled': props.isDisabled || isOpeningLocationSelector }"
      role="button"
      tabindex="0"
      :aria-disabled="props.isDisabled || isOpeningLocationSelector"
      @click="openLocationSelector"
      @keydown.enter.prevent="openLocationSelector"
      @keydown.space.prevent="openLocationSelector"
    >
      <div class="post-locations__empty-copy">
        <IconEmbedded class="post-locations__empty-icon" name="tag-person_2" :size="36" color="#667085" />
        <div>
          <strong>Tag an Account</strong>
          <small>Mention it or tag in photos</small>
        </div>
      </div>
      <SrpButton size="small" fill="outlined" color="gray" :isDisabled="props.isDisabled || isOpeningLocationSelector" @click.stop="openLocationSelector">
        <template #icon>
          <LoadingIcon v-if="isOpeningLocationSelector" :size="14" />
          <IconEmbedded v-else name="plus_3" :size="14" />
        </template>
        Tag Account
      </SrpButton>
    </div>

    <div class="post-locations__advanced">
      <button type="button" class="post-locations__advanced-trigger" :aria-expanded="isAdvancedExpanded" @click="isAdvancedExpanded = !isAdvancedExpanded">
        <span class="post-locations__advanced-summary">
          <IconEmbedded name="user-plus_2-5" :size="18" />
          <span :style="{ fontWeight: collaborators.length ? 600 : 500 }">{{ collaboratorSummary }}</span>
        </span>
      </button>
      <div v-if="isAdvancedExpanded" class="post-locations__advanced-content">
        <div class="post-locations__advanced-heading">
          <IconEmbedded name="instagram" :size="18" color="#667085" />
          <label for="instagram-collaborator-input"><strong>Instagram collaborator</strong></label>
        </div>
        <div class="post-locations__collaborator-input" :class="{ 'post-locations__collaborator-input--disabled': collaboratorControlsDisabled }">
          <span v-for="handle in collaborators" :key="handle" class="post-locations__chip">
            @{{ handle }}
            <button type="button" :aria-label="`Remove @${handle}`" :disabled="collaboratorControlsDisabled" @click="removeCollaborator(handle)">×</button>
          </span>
          <input
            id="instagram-collaborator-input"
            v-model="collaboratorInput"
            type="text"
            maxlength="31"
            placeholder="@username"
            :disabled="collaboratorControlsDisabled"
            :readonly="collaborators.length >= maximumInstagramCollaborators"
            @keydown="handleCollaboratorKeydown"
            @blur="commitCollaboratorInput"
            @input="collaboratorError = ''"
          />
          <LoadingIcon v-if="isCheckingCollaborator" class="post-locations__collaborator-loader" :size="16" />
        </div>
        <div v-if="collaboratorTestLinks.length" class="post-locations__collaborator-test-links">
          <LinkWithIcon
            v-for="link in collaboratorTestLinks"
            :key="link.handle"
            class="post-locations__collaborator-test-link"
            tag="a"
            color="gray"
            isDottedUnderline
            target="_blank"
            rel="noopener noreferrer"
            :href="link.href"
          >
            <span>{{ link.label }}</span>
            <template #iconRight>
              <IconEmbedded name="external-link_2-5" :size="14" color="rgba(0, 0, 0, 0.3)" />
            </template>
          </LinkWithIcon>
        </div>
        <div v-if="!hasInstagram" class="post-locations__advanced-hint">Select Instagram to invite collaborators.</div>
        <div v-else class="post-locations__advanced-hint-row">
          <span class="post-locations__advanced-hint">They'll receive an invitation when the post is published.</span>
          <LinkWithIcon
            color="gray"
            isDottedUnderline
            :aria-expanded="isCollaboratorMoreInfoExpanded"
            aria-controls="collaborator-more-info"
            @click="isCollaboratorMoreInfoExpanded = !isCollaboratorMoreInfoExpanded"
          >
            <!-- <template #icon><IconEmbedded name="info_2-5" :size="16" /></template> -->
            <span>{{ isCollaboratorMoreInfoExpanded ? "show less" : "more info" }}</span>
          </LinkWithIcon>
        </div>
        <div v-if="hasInstagram && isCollaboratorMoreInfoExpanded" id="collaborator-more-info" class="post-locations__collaborator-more-info">
          <strong>Collaborative Posts (from Meta)</strong>
          <ul>
            <li>If your collaborator accepts, their name will be added to your post and it will be shared on their page.</li>
            <li>Your collaborator will be able to see aggregated insights for this content.</li>
            <li>If your collaborative post is eligible for monetization, earnings will be available only to the primary creator.</li>
            <li>You can remove your collaborator anytime. This will remove their name from your post and it won't be shared on their page.</li>
          </ul>
        </div>
        <p v-if="collaboratorError" class="global-error-string">{{ collaboratorError }}</p>
      </div>
    </div>

    <LocationPickerModal
      v-model:isVisible="isPickerVisible"
      :customerId="props.customerId"
      :excludeLocationIds="settings.map(item => item.locationId)"
      :reloadKey="pickerReloadKey"
      @select="addLocation"
      @create="openCreate"
      @edit="openEdit"
    />
    <LocationEditorModal v-model:isVisible="isEditorVisible" :customerId="props.customerId" :location="locationToEdit" @saved="handleLocationSaved" />
  </section>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpDropdown from "@components/ui/SrpDropdown.vue";
import SrpSwitch from "@components/ui/SrpSwitch.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import Loader from "@components/Loader/Loader.vue";
import LocationAvatar from "./LocationAvatar.vue";
import LocationSocialHandles from "./LocationSocialHandles.vue";
import LocationPickerModal from "./LocationPickerModal.vue";
import LocationEditorModal from "./LocationEditorModal.vue";
import SocialLocationsRepo from "@repos/SocialLocationsRepo";
import { SocialPostPublishType } from "@contracts/shareStop";
import type { SocialChannels } from "@contracts/socialNetworks";
import { InstagramPhotosToTag, type LocationData, type SocialPostLocationTagSettingsData, type TaggableLocation } from "@contracts/socialLocations";
import { buildInstagramProfileUrl } from "@logic/SocialFieldInputCleaner";
import { MAX_TAGGED_LOCATIONS } from "@/constants/social";

const props = withDefaults(
  defineProps<{
    customerId: string;
    locationTagSettings?: SocialPostLocationTagSettingsData[];
    physicalLocationId?: string | null;
    instagramCollaboratorHandles?: string[];
    postType?: SocialPostPublishType | string | null;
    assetIds?: string[];
    selectedChannels?: SocialChannels[];
    isDisabled?: boolean;
    collapseKey?: string | number | null;
  }>(),
  {
    locationTagSettings: () => [],
    physicalLocationId: null,
    instagramCollaboratorHandles: () => [],
    postType: SocialPostPublishType.Post,
    assetIds: () => [],
    selectedChannels: () => [],
    isDisabled: false,
    collapseKey: null,
  }
);
const emit = defineEmits<{
  (e: "update:locationTagSettings", value: SocialPostLocationTagSettingsData[]): void;
  (e: "update:physicalLocationId", value: string | null): void;
  (e: "update:instagramCollaboratorHandles", value: string[]): void;
  (e: "update:isCollaboratorValidationBusy", value: boolean): void;
}>();

const settings = computed(() => props.locationTagSettings ?? []);
const collaborators = computed(() => props.instagramCollaboratorHandles ?? []);
const locationMap = ref<Map<string, LocationData>>(new Map());
const isLoading = ref(false);
const isPickerVisible = ref(false);
const isEditorVisible = ref(false);
const isOpeningLocationSelector = ref(false);
const pickerReloadKey = ref(0);
const locationToEdit = ref<LocationData | null>(null);
/** True when the editor was opened from the picker (edit or create), so cancel returns to the list. */
const openedEditorFromPicker = ref(false);
const hasInstagram = computed(() => props.selectedChannels.includes("instagram"));
const isAtTaggedLocationLimit = computed(() => settings.value.length >= MAX_TAGGED_LOCATIONS);
const maximumInstagramCollaborators = 3;
const isAdvancedExpanded = ref(false);
const isCollaboratorMoreInfoExpanded = ref(false);
const collapsedOptionLocationIds = ref<Set<string>>(new Set());
const collaboratorInput = ref("");
const collaboratorError = ref("");
const isCheckingCollaborator = ref(false);
const collaboratorLookupDisabled = computed(() => Boolean(props.isDisabled || !hasInstagram.value));
const collaboratorControlsDisabled = computed(() => collaboratorLookupDisabled.value || isCheckingCollaborator.value);
const collaboratorSummary = computed(() => (collaborators.value.length === 0 ? "Invite collaborator" : `Collaborating with ${collaborators.value.map(handle => `@${handle}`).join(", ")}`));
const collaboratorTestLinks = computed(() => {
  const handles = collaborators.value.map(handle => handle.trim()).filter(Boolean);
  return handles.map(handle => ({
    handle,
    href: buildInstagramProfileUrl(handle),
    label: handles.length === 1 ? `instagram.com/${handle}` : `@${handle}`,
  }));
});
let collaboratorCommitInFlight: Promise<boolean> | null = null;
let collaboratorValidationSequence = 0;
const selectedLocationIds = computed(() => settings.value.map(item => item.locationId).filter(Boolean));
const selectedLocationResolveKey = computed(() => (selectedLocationIds.value.length ? JSON.stringify([props.customerId, selectedLocationIds.value]) : ""));
let resolvedLocationKey = "";
let pendingResolveKey = "";
let resolveRequestSequence = 0;

watch(
  () => props.collapseKey,
  () => {
    isAdvancedExpanded.value = false;
    isCollaboratorMoreInfoExpanded.value = false;
    // Mention and photo-tag options start expanded; users can still collapse a card.
    collapsedOptionLocationIds.value = new Set();
    collaboratorInput.value = "";
    collaboratorError.value = "";
    isCheckingCollaborator.value = false;
    collaboratorValidationSequence += 1;
  },
  { immediate: true }
);

watch(isAdvancedExpanded, expanded => {
  if (!expanded) isCollaboratorMoreInfoExpanded.value = false;
});

watch(isCheckingCollaborator, busy => emit("update:isCollaboratorValidationBusy", busy), { immediate: true });

onUnmounted(() => {
  collaboratorValidationSequence += 1;
  emit("update:isCollaboratorValidationBusy", false);
});

watch(
  selectedLocationResolveKey,
  async key => {
    const ids = selectedLocationIds.value;
    if (!key || !ids.length) {
      locationMap.value = new Map();
      resolvedLocationKey = "";
      pendingResolveKey = "";
      return;
    }
    if (key === resolvedLocationKey || key === pendingResolveKey) return;
    if (ids.every(id => locationMap.value.has(id))) {
      resolvedLocationKey = key;
      return;
    }
    const sequence = ++resolveRequestSequence;
    pendingResolveKey = key;
    isLoading.value = true;
    try {
      const locations = await SocialLocationsRepo.resolve(props.customerId, ids);
      if (sequence !== resolveRequestSequence) return;
      locationMap.value = new Map(locations.map(location => [location.id, location]));
      resolvedLocationKey = key;
    } finally {
      if (pendingResolveKey === key) pendingResolveKey = "";
      if (sequence === resolveRequestSequence) isLoading.value = false;
    }
  },
  { immediate: true }
);

function replaceSetting(locationId: string, updater: (value: SocialPostLocationTagSettingsData) => SocialPostLocationTagSettingsData) {
  emit(
    "update:locationTagSettings",
    settings.value.map(item => (item.locationId === locationId ? updater({ ...item }) : item))
  );
}

function addLocation(location: TaggableLocation) {
  if (isAtTaggedLocationLimit.value) return;
  if (settings.value.some(item => item.locationId === location.locationId)) return;
  const supportsTag = location.canInstagramTag && hasInstagram.value && props.postType === SocialPostPublishType.Post;
  const supportsMention = props.selectedChannels.some(
    channel =>
      (channel === "instagram" && Boolean(location.instagramHandle)) || (channel === "facebook" && Boolean(location.facebookPageName)) || (channel === "tiktok" && Boolean(location.tikTokHandle))
  );
  emit("update:locationTagSettings", [
    ...settings.value,
    {
      locationId: location.locationId,
      addCaptionMention: supportsMention,
      instagramTag: supportsTag ? { photosToTag: InstagramPhotosToTag.First } : null,
    },
  ]);
  if (collapsedOptionLocationIds.value.has(location.locationId)) {
    const next = new Set(collapsedOptionLocationIds.value);
    next.delete(location.locationId);
    collapsedOptionLocationIds.value = next;
  }
}

function setMention(locationId: string, enabled: boolean) {
  replaceSetting(locationId, value => ({ ...value, addCaptionMention: enabled }));
}
function setInstagramTag(locationId: string, enabled: boolean) {
  replaceSetting(locationId, value => ({
    ...value,
    instagramTag: enabled ? { photosToTag: effectivePhotosToTag(value) } : null,
  }));
}

function effectivePhotosToTag(setting: SocialPostLocationTagSettingsData): InstagramPhotosToTag {
  return setting.instagramTag?.photosToTag ?? InstagramPhotosToTag.First;
}

function photosToTagSummary(setting: SocialPostLocationTagSettingsData): string {
  return effectivePhotosToTag(setting) === InstagramPhotosToTag.All ? "All photos" : "First photo";
}

function photosToTagLabel(setting: SocialPostLocationTagSettingsData): string {
  return `Tag ${photosToTagSummary(setting).toLowerCase()}`;
}

function setPhotosToTag(locationId: string, photosToTag: InstagramPhotosToTag) {
  replaceSetting(locationId, value => ({
    ...value,
    instagramTag: value.instagramTag ? { ...value.instagramTag, photosToTag } : { photosToTag },
  }));
}

function removeLocation(locationId: string) {
  emit(
    "update:locationTagSettings",
    settings.value.filter(item => item.locationId !== locationId)
  );
  // Physical-location selection has no V1 control, but API-created posts can still
  // contain one. Keep the backend invariant intact if its saved Location is removed.
  if (props.physicalLocationId === locationId) emit("update:physicalLocationId", null);
  if (collapsedOptionLocationIds.value.has(locationId)) {
    const next = new Set(collapsedOptionLocationIds.value);
    next.delete(locationId);
    collapsedOptionLocationIds.value = next;
  }
}

function isOptionsExpanded(locationId: string): boolean {
  return !collapsedOptionLocationIds.value.has(locationId);
}

function toggleOptionsExpanded(locationId: string) {
  const next = new Set(collapsedOptionLocationIds.value);
  if (next.has(locationId)) next.delete(locationId);
  else next.add(locationId);
  collapsedOptionLocationIds.value = next;
}

function handleCollaboratorKeydown(event: KeyboardEvent) {
  if (event.key === "Enter" || event.key === ",") {
    event.preventDefault();
    commitCollaboratorInput();
  } else if (event.key === "Backspace" && !collaboratorInput.value && collaborators.value.length > 0 && !collaboratorControlsDisabled.value) {
    emit("update:instagramCollaboratorHandles", collaborators.value.slice(0, -1));
  }
}

async function commitCollaboratorInput(): Promise<boolean> {
  if (collaboratorCommitInFlight) return collaboratorCommitInFlight;
  collaboratorCommitInFlight = runCollaboratorCommit().finally(() => {
    collaboratorCommitInFlight = null;
  });
  return collaboratorCommitInFlight;
}

async function runCollaboratorCommit(): Promise<boolean> {
  if (collaboratorLookupDisabled.value) return true;
  const values = collaboratorInput.value.split(",").map(normalizeCollaboratorHandle).filter(Boolean);
  if (values.length === 0) {
    collaboratorInput.value = "";
    return true;
  }
  const invalid = values.find(handle => !isValidInstagramHandle(handle));
  if (invalid) {
    collaboratorError.value = `Enter a valid Instagram handle instead of @${invalid}.`;
    return false;
  }
  const next = [...collaborators.value];
  const toValidate: string[] = [];
  for (const handle of values) {
    if (next.some(existing => existing.toLowerCase() === handle) || toValidate.includes(handle)) continue;
    if (next.length + toValidate.length >= maximumInstagramCollaborators) {
      collaboratorError.value = `Instagram supports at most ${maximumInstagramCollaborators} collaborators per post.`;
      return false;
    }
    toValidate.push(handle);
  }
  if (toValidate.length === 0) {
    collaboratorInput.value = "";
    collaboratorError.value = "";
    return true;
  }

  const sequence = ++collaboratorValidationSequence;
  isCheckingCollaborator.value = true;
  collaboratorError.value = "";
  try {
    const confirmed: string[] = [];
    for (const handle of toValidate) {
      const result = await SocialLocationsRepo.validateInstagram(props.customerId, handle, false);
      if (sequence !== collaboratorValidationSequence) return false;
      if (!result.isValid) {
        collaboratorError.value = "Couldn't find that Instagram account. Check the handle and try again.";
        return false;
      }
      // Instagram only lets public accounts be collaborators; adding a private one fails the whole post at publish.
      if (result.isPrivate === true) {
        collaboratorError.value = "Private accounts can't be Instagram collaborators.";
        return false;
      }
      const username = normalizeCollaboratorHandle(result.username || handle);
      if (!next.some(existing => existing.toLowerCase() === username) && !confirmed.includes(username)) confirmed.push(username);
    }
    emit("update:instagramCollaboratorHandles", [...next, ...confirmed]);
    collaboratorInput.value = "";
    collaboratorError.value = "";
    return true;
  } catch {
    if (sequence !== collaboratorValidationSequence) return false;
    collaboratorError.value = "Couldn't verify that Instagram account right now. Please try again.";
    return false;
  } finally {
    if (sequence === collaboratorValidationSequence) isCheckingCollaborator.value = false;
  }
}

async function ensureCollaboratorsValid(): Promise<boolean> {
  const ok = await commitCollaboratorInput();
  if (!ok) {
    isAdvancedExpanded.value = true;
    return false;
  }
  return true;
}

defineExpose({ ensureCollaboratorsValid, isCheckingCollaborator });

function removeCollaborator(handle: string) {
  if (collaboratorControlsDisabled.value) return;
  emit(
    "update:instagramCollaboratorHandles",
    collaborators.value.filter(value => value !== handle)
  );
}

function normalizeCollaboratorHandle(value: string): string {
  return value.trim().replace(/^@/, "").toLowerCase();
}

function isValidInstagramHandle(value: string): boolean {
  return value.length > 0 && value.length <= 30 && /^[a-z0-9._]+$/.test(value);
}

function openAdjust(locationId: string) {
  openedEditorFromPicker.value = false;
  locationToEdit.value = locationMap.value.get(locationId) ?? null;
  isEditorVisible.value = true;
}
function openCreate() {
  openedEditorFromPicker.value = true;
  locationToEdit.value = null;
  isEditorVisible.value = true;
}
async function openEdit(locationId: string) {
  openedEditorFromPicker.value = true;
  const cached = locationMap.value.get(locationId);
  if (cached) {
    locationToEdit.value = cached;
    isEditorVisible.value = true;
    return;
  }
  const [location] = await SocialLocationsRepo.resolve(props.customerId, [locationId]);
  locationToEdit.value = location ?? null;
  isEditorVisible.value = true;
}
async function openLocationSelector() {
  if (props.isDisabled || isOpeningLocationSelector.value || isAtTaggedLocationLimit.value) return;
  isOpeningLocationSelector.value = true;
  try {
    const excludedIds = new Set(settings.value.map(setting => setting.locationId));
    const locations = await SocialLocationsRepo.listTaggable(props.customerId);
    if (locations.some(location => !excludedIds.has(location.locationId))) {
      isPickerVisible.value = true;
    } else {
      openCreate();
    }
  } finally {
    isOpeningLocationSelector.value = false;
  }
}
async function handleLocationSaved(location: LocationData) {
  locationMap.value = new Map(locationMap.value).set(location.id, location);
  const isCreate = locationToEdit.value == null;
  const cameFromPicker = openedEditorFromPicker.value;
  openedEditorFromPicker.value = false;

  if (isCreate) {
    if (!isAtTaggedLocationLimit.value && !settings.value.some(item => item.locationId === location.id)) {
      addLocation({
        locationId: location.id,
        name: location.name,
        iconUrl: location.icon,
        instagramHandle: location.socialSet?.instagramHandle,
        instagramIsPrivate: location.socialSet?.instagramIsPrivate,
        facebookPageName: location.socialSet?.facebookPageName,
        tikTokHandle: location.socialSet?.tikTokHandle,
        canMention: true,
        canInstagramTag: Boolean(location.socialSet?.instagramHandle),
        canSetInstagramLocation: Boolean(location.instagramLocationId),
      });
    }
    // Return to the post editor after creating from the picker (or empty-list shortcut).
    if (cameFromPicker) isPickerVisible.value = false;
    return;
  }

  // Edit from picker: keep the list open and refresh so updated name/handles show; user can Set.
  if (cameFromPicker && isPickerVisible.value) {
    pickerReloadKey.value += 1;
  }
}
function isInstagramPrivate(location?: LocationData): boolean {
  return location?.socialSet?.instagramIsPrivate === true;
}
function canMention(location?: LocationData): boolean {
  if (!location) return false;
  return props.selectedChannels.some(
    channel =>
      (channel === "instagram" && Boolean(location.socialSet?.instagramHandle)) ||
      (channel === "facebook" && Boolean(location.socialSet?.facebookPageId)) ||
      (channel === "tiktok" && Boolean(location.socialSet?.tikTokHandle))
  );
}
function mentionNetworks(location?: LocationData): SocialChannels[] {
  if (!location) return [];
  return props.selectedChannels.filter(
    channel =>
      (channel === "instagram" && location.socialSet?.instagramHandle) || (channel === "facebook" && location.socialSet?.facebookPageId) || (channel === "tiktok" && location.socialSet?.tikTokHandle)
  );
}
function showInstagramTagRow(location?: LocationData): boolean {
  return Boolean(location?.socialSet?.instagramHandle && hasInstagram.value && props.postType === SocialPostPublishType.Post);
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

.post-locations {
  margin-top: 38px;
}
.post-locations__heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 10px;
}
.post-locations__title-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.post-locations__title {
  margin: 0;
}
.post-locations__max-hint {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #667085;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}
.post-locations__cards {
  display: grid;
  gap: 12px;
}
.post-locations__card {
  border: 1px solid #d7dce2;
  border-radius: 9px;
  overflow: hidden;
  background: white;
}
.post-locations__card-header {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 11px 14px;
  background: #f9fbfb;
  cursor: pointer;
}
.post-locations__title-copy {
  flex: 1;
  display: grid;
  gap: 2px;
  min-width: 0;
}
.post-locations__card-header button {
  border: 0;
  background: transparent;
  cursor: pointer;
}
.post-locations__header-action {
  // color: #806468;
  color: gray;
}
.post-locations__header-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.post-locations__card-header .post-locations__remove {
  padding-left: 12px;
  border-left: 1px solid #d8dce2;
}
.post-locations__collapse {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #667085;
  padding: 0 0 0 4px;
  pointer-events: none;
}
.post-locations__option {
  min-height: 54px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 15px;
  border-top: 1px solid #e4e7eb;
}
.post-locations__option > div {
  display: grid;
  gap: 2px;
}
.post-locations__option small {
  color: #667085;
}
.post-locations__private-note {
  margin: 0 15px 10px;
}
.post-locations__option-label {
  display: flex !important;
  align-items: center;
  gap: 8px;
}
.post-locations__instagram-tag-copy {
  display: grid !important;
  gap: 2px;
  min-width: 0;
}
.post-locations__instagram-tag-title {
  display: flex;
  align-items: center;
  gap: 6px;
}
.post-locations__tag-scope {
  display: inline-flex;
  align-items: center;
}
.post-locations__tag-scope--disabled {
  :deep(.dropdown-wrapper__clickable-element) {
    opacity: 0.55;
    pointer-events: none;
  }
}
.post-locations__gear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #344054;
  cursor: pointer;
}
.post-locations__gear:hover:not(:disabled) {
  background: #f2f4f7;
}
.post-locations__gear:disabled {
  color: #98a2b3;
  cursor: default;
}
.post-locations__tag-scope-menu {
  padding: 6px 0;
}
.post-locations__tag-scope-option {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 14px;
  border: 0;
  background: transparent;
  color: #101828;
  font-size: 14px;
  line-height: 1.3;
  text-align: left;
  cursor: pointer;
}
.post-locations__tag-scope-option:hover {
  background: #f9fafb;
}
.post-locations__mention-label {
  display: flex !important;
  align-items: center;
  gap: 7px;
}
.post-locations__network-icons {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.post-locations__empty {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border: 1px solid #d7dce2;
  border-radius: 9px;
  background: white;
  cursor: pointer;
}
.post-locations__empty--disabled {
  opacity: 0.6;
  cursor: default;
  pointer-events: none;
}
.post-locations__empty-copy {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.post-locations__empty-copy > div {
  display: grid;
  gap: 2px;
}
.post-locations__empty-copy strong {
  color: #344054;
  font-size: 14px;
  font-weight: 600;
}
.post-locations__empty-copy small {
  color: #667085;
  font-size: 13px;
  line-height: 1.35;
}
.post-locations__advanced {
  // margin-top: 16px;
  // border-top: 1px solid #e4e7eb;
}
.post-locations__advanced-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 0 2px;
  border: 0;
  background: transparent;
  color: gray;
  cursor: pointer;
  text-align: left;
}
.post-locations__advanced-summary {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}
.post-locations__advanced-trigger strong {
  color: #667085;
  font-size: 14px;
  font-weight: 600;
}
.post-locations__advanced-content {
  display: grid;
  gap: 10px;
  margin-top: 12px;
  padding: 16px;
  border: 1px solid #d7dce2;
  border-radius: 9px;
  background: white;
}
.post-locations__advanced-heading {
  display: flex;
  align-items: center;
  gap: 8px;
}
.post-locations__advanced-heading label {
  margin: 0;
}
.post-locations__collaborator-input {
  min-height: 42px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border: 1px solid #cbd0d7;
  border-radius: 7px;
  background: white;
}
.post-locations__collaborator-input--disabled {
  background: #f3f4f6;
}
.post-locations__collaborator-input input {
  flex: 1;
  min-width: 130px;
  padding: 5px;
  border: 0;
  outline: 0;
  background: transparent;
}
.post-locations__collaborator-loader {
  flex-shrink: 0;
  margin-right: 2px;
}
.post-locations__collaborator-test-links {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
}
.post-locations__collaborator-test-link {
  font-size: 13px;
}
.post-locations__chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 8px;
  border-radius: 999px;
  background: #e6f5f3;
  color: #086e70;
  font-size: 13px;
}
.post-locations__chip button {
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 17px;
  line-height: 1;
}
.post-locations__chip button:disabled {
  cursor: default;
  opacity: 0.5;
}
.post-locations__advanced-hint {
  color: #667085;
}
.post-locations__advanced-hint-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
}
.post-locations__collaborator-more-info {
  display: grid;
  gap: 8px;
  color: #667085;
  font-size: 13px;
  line-height: 1.4;
}
.post-locations__collaborator-more-info strong {
  color: #344054;
  font-size: 13px;
  font-weight: 600;
}
.post-locations__collaborator-more-info ul {
  margin: 0;
  padding-left: 18px;
  display: grid;
  gap: 6px;
}
.post-locations__advanced-content .global-error-string {
  margin: 0;
}

@media (max-width: $mobile-max-width) {
  .post-locations__empty-icon {
    display: none;
  }

  .post-locations__header-actions {
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
  }

  .post-locations__card-header .post-locations__remove {
    padding-left: 0;
    border-left: 0;
  }
}
</style>
